import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  TextField,
} from "@mui/material";
import { ErrorAlert } from "components/alert/ErrorAlert";

type CancelSubscriptionDialogProps = {
  open: boolean;
  subscriptionId: string;
  subscriptionName: string;
  handleClose: () => void;
  submitCancel: (reason: string) => Promise<void>;
};

export function CancelSubscriptionDialog(props: CancelSubscriptionDialogProps) {
  const [reason, setReason] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<unknown | undefined>();

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError(new Error("A reason is required"));
      return;
    }

    setLoading(true);
    setError(undefined);

    try {
      await props.submitCancel(reason);
      props.handleClose();
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setReason("");
    setError(undefined);
    props.handleClose();
  };

  return (
    <Dialog
      open={props.open}
      onClose={() => {
        if (!loading) handleCancel();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Expire Subscription</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {error ? <ErrorAlert error={error} /> : null}
          <Grid size={12}>
            <DialogContentText>
              Are you sure you want to expire the subscription <strong>{props.subscriptionName}</strong>? It can be
              reinstated later if needed.
            </DialogContentText>
          </Grid>
          <Grid size={12}>
            <TextField
              autoFocus
              required
              fullWidth
              multiline
              rows={4}
              label="Reason"
              placeholder="Please provide a reason for expiring this subscription..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              error={!reason.trim() && error !== undefined}
              helperText={!reason.trim() && error !== undefined ? "Reason is required" : ""}
              disabled={loading}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="error" disabled={loading || !reason.trim()}>
          {loading ? "Expiring..." : "Expire Subscription"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
